import { type ApiClient, useApiClient } from "@albertine/api-client";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffectEvent, useState } from "react";
import { assertNever } from "../-utils/chatErrors";
import {
    type ChatThreadInfiniteData,
    type ChatThreadPage,
    chatThreadPagesContainMessage,
    EMPTY_CHAT_MESSAGES,
    EMPTY_UNREAD_MESSAGE_IDS,
    flattenChatThreadPages,
} from "../-utils/threadCache";
import type { ChatScope } from "./chatApi";

const INITIAL_MESSAGE_LIMIT = "100";

const NO_REQUEST_ID = "__none__";

const MAX_MESSAGE_LOOKUP_PAGES = 20;

export type ChatThreadQueryKey = ReturnType<typeof getChatThreadQueryKey>;

export function getChatThreadQueryKey(scope: ChatScope) {
    switch (scope.type) {
        case "general":
            return ["generalChatMessages", generalChatParams()] as const;
        case "request":
            return ["requestChatMessages", requestChatParams(scope.requestId)] as const;
        default:
            return assertNever(scope);
    }
}

export function useChatThread(scope: ChatScope) {
    const { apiClient } = useApiClient();
    const queryKey = getChatThreadQueryKey(scope);
    const [previousFetchFailed, setPreviousFetchFailed] = useState(false);

    const generalQuery = useChatThreadInfiniteQuery(apiClient, {
        type: "general",
        enabled: scope.type === "general",
    });

    const requestQuery = useChatThreadInfiniteQuery(apiClient, {
        type: "request",
        requestId: scope.type === "request" ? scope.requestId : NO_REQUEST_ID,
        enabled: scope.type === "request",
    });

    const activeQuery = scope.type === "request" ? requestQuery : generalQuery;
    const threadPage = flattenChatThreadPages(activeQuery.data);
    const messages = threadPage?.messages ?? EMPTY_CHAT_MESSAGES;
    const canFetchPrevious = Boolean(activeQuery.hasNextPage) && !previousFetchFailed;

    const fetchPreviousMessages = useEffectEvent(() => {
        if (
            !activeQuery.hasNextPage ||
            previousFetchFailed ||
            activeQuery.isFetchingNextPage
        ) {
            return;
        }

        void activeQuery.fetchNextPage().then((result) => {
            if (result.isError) {
                setPreviousFetchFailed(true);
            }
        });
    });

    const resetPreviousFetchFailure = useEffectEvent(() => {
        setPreviousFetchFailed(false);
    });

    const fetchPreviousUntilMessageLoaded = useEffectEvent(
        async (messageId: string) => {
            if (chatThreadPagesContainMessage(activeQuery.data, messageId)) {
                return true;
            }

            let hasMorePages = activeQuery.hasNextPage;

            for (
                let page = 0;
                page < MAX_MESSAGE_LOOKUP_PAGES && hasMorePages;
                page++
            ) {
                const result = await activeQuery.fetchNextPage();

                if (result.isError) {
                    setPreviousFetchFailed(true);
                    return false;
                }

                if (chatThreadPagesContainMessage(result.data, messageId)) {
                    return true;
                }

                hasMorePages = result.hasNextPage;
            }

            return false;
        },
    );

    return {
        messages,
        conversationId: threadPage?.conversationId ?? null,
        unreadMessageIds: threadPage?.unreadMessageIds ?? EMPTY_UNREAD_MESSAGE_IDS,
        canFetchPrevious,
        isFetchingPrevious: activeQuery.isFetchingNextPage,
        fetchPreviousMessages,
        fetchPreviousUntilMessageLoaded,
        resetPreviousFetchFailure,
        queryKey,
        refetch: activeQuery.refetch,
        // Next-page fetches must not re-trigger unread seeding.
        isFetching: activeQuery.isFetching && !activeQuery.isFetchingNextPage,
        isFetchedAfterMount: activeQuery.isFetchedAfterMount,
        isPending: activeQuery.isPending,
        isError: activeQuery.isError,
        error: activeQuery.error,
    };
}

function useChatThreadInfiniteQuery(
    apiClient: ApiClient,
    scope:
        | { type: "general"; enabled: boolean }
        | { type: "request"; requestId: string; enabled: boolean },
) {
    const queryKey =
        scope.type === "general"
            ? getChatThreadQueryKey({ type: "general" })
            : getChatThreadQueryKey({ type: "request", requestId: scope.requestId });

    return useInfiniteQuery<
        ChatThreadPage,
        unknown,
        ChatThreadInfiniteData,
        ChatThreadQueryKey,
        string | undefined
    >({
        queryKey,
        enabled: scope.enabled,
        refetchOnMount: "always",
        initialPageParam: undefined,
        queryFn: ({ pageParam }) =>
            fetchChatMessagesPage(
                apiClient,
                scope.type === "general"
                    ? { type: "general" }
                    : { type: "request", requestId: scope.requestId },
                pageParam,
            ),
        getNextPageParam,
    });
}

function getNextPageParam(lastPage: ChatThreadPage): string | undefined {
    if (lastPage.messages.length === 0) {
        return undefined;
    }

    return lastPage.nextPageCursor ?? undefined;
}

async function fetchChatMessagesPage(
    apiClient: ApiClient,
    scope: ChatScope,
    cursor: string | undefined,
): Promise<ChatThreadPage> {
    switch (scope.type) {
        case "general": {
            const response = await apiClient.get.generalChatMessages({
                query: cursor
                    ? { limit: INITIAL_MESSAGE_LIMIT, cursor }
                    : { limit: INITIAL_MESSAGE_LIMIT },
            });
            const data = await response.json();
            if (!data.success) {
                throw data;
            }
            return data.data;
        }
        case "request": {
            const response = await apiClient.get.requestChatMessages({
                param: { requestId: scope.requestId },
                query: cursor
                    ? { limit: INITIAL_MESSAGE_LIMIT, cursor }
                    : { limit: INITIAL_MESSAGE_LIMIT },
            });
            const data = await response.json();
            if (!data.success) {
                throw data;
            }
            return data.data;
        }
        default:
            return assertNever(scope);
    }
}

function generalChatParams() {
    return { query: { limit: INITIAL_MESSAGE_LIMIT } };
}

function requestChatParams(requestId: string) {
    return { param: { requestId }, query: { limit: INITIAL_MESSAGE_LIMIT } };
}
